import { DOMBuilder } from "./core/DOMBuilder";
import { OptionResolver } from "./core/OptionResolver";
import { StateReducer } from "./core/StateReducer";
import { ToggleActionType } from "./core/StateReducer.types";
var Toggle = /** @class */ (function () {
    /**
   * Initializes a new instance of the BootstrapToggle class.
   * @param element The HTMLInputElement element which represents the toggle.
   * @param options The options for the toggle.
   * @returns The constructed BootstrapToggle instance.
   */
    function Toggle(element, options) {
        var _this = this;
        this.pointer = null;
        this.SCROLL_THRESHOLD = 10;
        this.eventsBound = false;
        this.suppressExternalSync = false;
        this.originalDescriptors = new Map();
        /**
         * Handles the change event of the input element of the toggle.
         * This event listener is responsible for detecting when the input element
         * of the toggle changes its state and triggering the update method to keep the toggle in sync.
         */
        this.onExternalChange = function () {
            _this.update(true);
        };
        this.onFormReset = function () {
            setTimeout(function () { return _this.onExternalChange(); }, 0);
        };
        /**
       * Handles pointer down events by initiating the toggle action and setting up
       * listeners for pointer movement, release, and cancellation.
       *
       * The method early exits if:
       * - the pointer event is not a primary mouse button click
       * - the toggle cannot be interacted with (`disabled` or `readonly`)
       * @param e The PointerEvent object representing the pointer down event.
       */
        this.onPointerDown = function (e) {
            if (e.pointerType === "mouse" && e.button !== 0)
                return;
            if (!_this.stateReducer.canInteract())
                return;
            _this.pointer = { x: e.clientX, y: e.clientY };
            _this.domBuilder.root.addEventListener("pointermove", _this.onPointerMove, {
                passive: true,
            });
            _this.domBuilder.root.addEventListener("pointerup", _this.onPointerUp, {
                passive: true,
            });
            _this.domBuilder.root.addEventListener("pointercancel", _this.onPointerCancel, { passive: true });
        };
        /**
       * Handles pointer move events by checking the distance moved from the initial pointer down position.
       * If the pointer has moved beyond a certain threshold, the pointer interaction is cancelled.
       *
       * Allows dragging within the width of the toggle but cancels if vertical movement exceeds the scroll threshold.
       * @param e The PointerEvent object representing the pointer move event.
       */
        this.onPointerMove = function (e) {
            var dx = Math.abs(e.clientX - _this.pointer.x);
            var dy = Math.abs(e.clientY - _this.pointer.y);
            if (dy > _this.SCROLL_THRESHOLD || dx > _this.domBuilder.root.offsetWidth) {
                _this.onPointerCancel();
            }
        };
        /**
       * Handles pointer up events by determining if the pointer interaction
       * should trigger a toggle action based on the distance moved.
       *
       * If the pointer has moved beyond a certain threshold, the pointer interaction is cancelled.
       * Allows dragging within the width of the toggle but cancels if vertical movement exceeds the scroll threshold.
       * Finally, it cleans up by calling the pointer cancel handler.
       *
       * If the pointer event is not a primary mouse button click, the interaction is cancelled.
       * @param e The PointerEvent object representing the pointer up event.
       */
        this.onPointerUp = function (e) {
            if (e.pointerType === "mouse" && e.button !== 0) {
                _this.onPointerCancel();
                return;
            }
            var dx = Math.abs(e.clientX - _this.pointer.x);
            var dy = Math.abs(e.clientY - _this.pointer.y);
            if (dy <= _this.SCROLL_THRESHOLD && dx <= _this.domBuilder.root.offsetWidth) {
                _this.apply(ToggleActionType.NEXT);
            }
            _this.onPointerCancel();
        };
        /**
       * Cleans up pointer event listeners after a pointer interaction is completed or cancelled.
       *
       * This method removes the `pointermove`, `pointerup`, and `pointercancel` event listeners
       * from the root element of the toggle.
       * However, `pointerdown` listener remains active for future interactions.
       */
        this.onPointerCancel = function () {
            _this.domBuilder.root.removeEventListener("pointermove", _this.onPointerMove);
            _this.domBuilder.root.removeEventListener("pointerup", _this.onPointerUp);
            _this.domBuilder.root.removeEventListener("pointercancel", _this.onPointerCancel);
        };
        this.handlerKeyboardEvent = function (e) {
            if (e.key == " ") {
                _this.apply(ToggleActionType.NEXT);
            }
        };
        this.handlerLabelEvent = function (e) {
            e.preventDefault();
            _this.apply(ToggleActionType.NEXT);
            _this.domBuilder.root.focus();
        };
        this.element = element;
        this.userOptions = options;
        this.options = OptionResolver.resolve(element, options);
        this.stateReducer = new StateReducer(element, this.options.tristate);
        this.domBuilder = new DOMBuilder(element, this.options, this.stateReducer.get());
        this.bindEventListeners();
        this.interceptInputProperties();
        this.element.bsToggle = this;
    }
    /**
     * Intercepts the following input properties to detect external changes:
     * - checked
     * - disabled
     * - readonly
     * - indeterminate
     * This method is used to detect changes made to the input element directly,
     * rather than through the BootstrapToggle API. It is used to maintain the
     * state of the toggle in cases where the user changes the input element
     * directly, rather than through the API.
     * @returns void
     */
    Toggle.prototype.interceptInputProperties = function () {
        var _this = this;
        var props = ["checked", "disabled", "readOnly", "indeterminate"];
        props.forEach(function (prop) {
            var descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(_this.element), prop);
            if (!(descriptor === null || descriptor === void 0 ? void 0 : descriptor.set))
                return;
            _this.originalDescriptors.set(prop, descriptor);
            Object.defineProperty(_this.element, prop, {
                configurable: true,
                get: function () { return descriptor.get.call(_this.element); },
                set: function (value) {
                    descriptor.set.call(_this.element, value);
                    if (_this.suppressExternalSync)
                        return;
                    _this.onExternalChange();
                },
            });
        });
    };
    /**
     * Restores the original input properties of the toggle element.
     * This method is used to restore the original descriptors of the input properties
     * which were intercepted by the BootstrapToggle to detect external changes.
     * @returns void
     */
    Toggle.prototype.restoreInputProperties = function () {
        var _this = this;
        this.originalDescriptors.forEach(function (descriptor, prop) {
            Object.defineProperty(_this.element, prop, descriptor);
        });
        this.originalDescriptors.clear();
    };
    /**
   * Binds event listeners to the toggle element.
   * This method is called by the constructor and is responsible for
   * binding the following event listeners:
   * - Pointer events (click, touchstart, touchend)
   * - Keyboard events (keydown, keyup)
   * - Label events (click)
   * If the event listeners are already bound (i.e. this.eventsBound is true),
   * this method does nothing.
   * @returns void
   */
    Toggle.prototype.bindEventListeners = function () {
        if (this.eventsBound)
            return;
        this.bindFormResetListener();
        this.bindPointerEventListener();
        this.bindKeyboardEventListener();
        this.bindLabelEventListener();
        this.eventsBound = true;
    };
    /**
   * Unbinds all event listeners from the toggle element.
   * This method is called by the destructor and is responsible for
   * unbinding the following event listeners:
   * - Pointer events (click, touchstart, touchend)
   * - Keyboard events (keydown, keyup)
   * - Label events (click)
   * If the event listeners are not bound (i.e. this.eventsBound is false),
   * this method does nothing.
   * @returns void
   */
    Toggle.prototype.unbindEventListeners = function () {
        if (!this.eventsBound)
            return;
        this.unbindFormResetListener();
        this.unbindPointerEventListener();
        this.unbindKeyboardEventListener();
        this.unbindLabelEventListener();
        this.eventsBound = false;
    };
    Toggle.prototype.bindFormResetListener = function () {
        var form = this.element.form;
        if (!form)
            return;
        form.addEventListener("reset", this.onFormReset);
    };
    Toggle.prototype.unbindFormResetListener = function () {
        var form = this.element.form;
        if (!form)
            return;
        form.removeEventListener("reset", this.onFormReset);
    };
    /**
   * Binds a pointerdown event listener to the root element of the toggle.
   * The event listener is responsible for handling pointer events (e.g. mouse clicks, touch events)
   * and triggering the toggle's state change when a pointer event occurs.
   * The event listener is bound with the passive option, which means that it will not block
   * other event listeners from being triggered.
   */
    Toggle.prototype.bindPointerEventListener = function () {
        this.domBuilder.root.addEventListener("pointerdown", this.onPointerDown, {
            passive: true,
        });
    };
    /**
   * Unbinds the pointerdown event listener from the root element of the toggle.
   * This method is responsible for unbinding the pointerdown event listener that was
   * previously bound by the bindPointerEventListener method.
   * If the event listener is not bound (i.e. this.eventsBound is false), this method does nothing.
   * @returns void
   */
    Toggle.prototype.unbindPointerEventListener = function () {
        this.domBuilder.root.removeEventListener("pointerdown", this.onPointerDown);
    };
    /**
   * Binds a keypress event listener to the root element of the toggle.
   * The event listener is responsible for handling keypress events
   * and triggering the toggle's state change when a keypress event occurs.
   * The event listener is bound with the passive option, which means that it will not block
   * other event listeners from being triggered.
   */
    Toggle.prototype.bindKeyboardEventListener = function () {
        this.domBuilder.root.addEventListener("keypress", this.handlerKeyboardEvent, { passive: true });
    };
    /**
   * Unbinds the keypress event listener from the root element of the toggle.
   * This method is responsible for unbinding the keypress event listener that was
   * previously bound by the bindKeyboardEventListener method.
   * If the event listener is not bound (i.e. this.eventsBound is false), this method does nothing.
   * @returns void
   */
    Toggle.prototype.unbindKeyboardEventListener = function () {
        this.domBuilder.root.removeEventListener("keypress", this.handlerKeyboardEvent);
    };
    /**
   * Binds a click event listener to all labels that are associated with the toggle's input element.
   * The event listener is responsible for handling click events and triggering the toggle's state change when a click event occurs.
   * The event listener is bound with the passive option set to false, which means that it will block other event listeners from being triggered until it has finished its execution.
   * This method is called by the constructor and is responsible for binding the event listener to the toggle's labels.
   * If the toggle's input element does not have an id (i.e. this.element.id is null or undefined), this method does nothing.
   * @returns void
   */
    Toggle.prototype.bindLabelEventListener = function () {
        var _this = this;
        if (this.element.id) {
            document
                .querySelectorAll('label[for="' + this.element.id + '"]')
                .forEach(function (label) {
                label.addEventListener("click", _this.handlerLabelEvent, {
                    passive: false,
                });
            });
        }
    };
    /**
   * Unbinds the click event listener from all labels that are associated with the toggle's input element.
   * This method is responsible for unbinding the event listener that was previously bound by the bindLabelEventListener method.
   * If the toggle's input element does not have an id (i.e. this.element.id is null or undefined), this method does nothing.
   * @returns void
   */
    Toggle.prototype.unbindLabelEventListener = function () {
        var _this = this;
        if (this.element.id) {
            document
                .querySelectorAll('label[for="' + this.element.id + '"]')
                .forEach(function (label) {
                label.removeEventListener("click", _this.handlerLabelEvent);
            });
        }
    };
    /**
   * Applies a toggle action to the toggle state and renders the toggle element.
   * If the action is successful, this method will render the toggle element with the new state.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param action The toggle action to apply.
   * @param silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.apply = function (action, silent) {
        if (silent === void 0) { silent = false; }
        if (!this.stateReducer.do(action))
            return;
        this.suppressExternalSync = true;
        try {
            this.domBuilder.render(this.stateReducer.get());
            if (!silent)
                this.trigger();
        }
        finally {
            this.suppressExternalSync = false;
        }
    };
    /**
   * Toggles the state of the toggle.
   * If the toggle is currently in the on state, it will be set to the off state.
   * If the toggle is currently in the off state, it will be set to the on state.
   * If the toggle is currently in the indeterminate state, it will be set to the on state.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.toggle = function (silent) {
        if (silent === void 0) { silent = false; }
        this.apply(ToggleActionType.TOGGLE, silent);
    };
    /**
   * Sets the toggle state to on.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.on = function (silent) {
        if (silent === void 0) { silent = false; }
        this.apply(ToggleActionType.ON, silent);
    };
    /**
   * Sets the toggle state to off.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.off = function (silent) {
        if (silent === void 0) { silent = false; }
        this.apply(ToggleActionType.OFF, silent);
    };
    /**
   * Sets the toggle state to indeterminate.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.indeterminate = function (silent) {
        if (silent === void 0) { silent = false; }
        this.apply(ToggleActionType.INDETERMINATE, silent);
    };
    /**
   * Sets the toggle state to determinate.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
   */
    Toggle.prototype.determinate = function (silent) {
        if (silent === void 0) { silent = false; }
        this.apply(ToggleActionType.DETERMINATE, silent);
    };
    /**
   * Enables the toggle.
   * If the toggle is currently disabled, this method will set the toggle state to enabled.
   * If the silent parameter is false, this method will also trigger the change event.
   * @returns void
   */
    Toggle.prototype.enable = function () {
        this.apply(ToggleActionType.ENABLE);
    };
    /**
   * Disables the toggle.
   * If the toggle is currently enabled, this method will set the toggle state to disabled.
   * If the silent parameter is false, this method will also trigger the change event.
   */
    Toggle.prototype.disable = function () {
        this.apply(ToggleActionType.DISABLE);
    };
    /**
   * Sets the toggle state to readonly.
   * If the toggle is currently disabled or enabled, this method will set the toggle state to readonly.
   * If the silent parameter is false, this method will also trigger the change event.
   * @returns void
   */
    Toggle.prototype.readonly = function () {
        this.apply(ToggleActionType.READONLY);
    };
    /**
   * Synchronizes the toggle state with the input element and renders the toggle.
   * If the silent parameter is false, this method will also trigger the change event.
   * @param {boolean} silent A boolean indicating whether to trigger the change event after synchronizing the toggle state.
   */
    Toggle.prototype.update = function (silent) {
        this.suppressExternalSync = true;
        try {
            this.stateReducer.sync(this.element);
            this.domBuilder.render(this.stateReducer.get());
            if (!silent)
                this.trigger();
        }
        finally {
            this.suppressExternalSync = false;
        }
    };
    /**
   * Triggers the change event on the toggle's input element.
   * @param {boolean} silent A boolean indicating whether to trigger the change event.
   * If the silent parameter is false, this method will trigger the change event.
   */
    Toggle.prototype.trigger = function (silent) {
        if (silent === void 0) { silent = false; }
        if (!silent)
            this.element.dispatchEvent(new Event("change", { bubbles: true }));
    };
    /**
   * Destroys the toggle element and unbinds all event listeners.
   *This method is useful when you need to remove the toggle element from the DOM.
   *After calling this method, the toggle element will be removed from the DOM and all event listeners will be unbound.
   */
    Toggle.prototype.destroy = function () {
        this.restoreInputProperties();
        this.unbindEventListeners();
        this.domBuilder.destroy();
        delete this.element.bsToggle;
    };
    /**
   * Destroys the toggle element and reinitializes it with the same options.
   *This method is useful when you need to reinitialize the toggle element with the same options.
   */
    Toggle.prototype.rerender = function () {
        this.destroy();
        var _ = new Toggle(this.element, this.userOptions);
    };
    return Toggle;
}());
export { Toggle };
